import { RouteObject } from 'react-router-dom'

// Re-roots a route tree written with absolute paths so it can be mounted under
// a non-root parent — specifically the brand page set under
// /portal/:brandSlug/* (TDC-1609).
//
// React Router resolves a nested route's path against its parent's
// pathnameBase. The brand trees' absolute paths ('/videos',
// '/video-production/projects', '/settings/*') only work today because they are
// mounted by <Home/> at '/*', where the base is '/'. Under 'portal/:brandSlug'
// the base becomes '/portal/<slug>', so every child path has to be relative.
//
// THIS FUNCTION MUST STAY PURE — never mutate the input.
// BrandProPaths, ExpertProfilePath and BrandTeamCreateProjectDistribution
// (components/Header/configuration/navs.tsx) are spread BY REFERENCE into both
// the brand routers and the internal ones (router/admin.tsx,
// router/internalRoutes.tsx). Cloning is what lets the brand mount re-root them
// without touching the internal mounts. Rewriting this to assign
// `route.path = ...` in place would corrupt those shared arrays at module-eval
// time, and the symptom — internal staff 404ing on /experts/:expertId/:slug —
// points nowhere near this file.
//
// Only `path` is rewritten. Redirect TARGETS (<Navigate to>) are element props,
// not paths, and are handled case-by-case: some must stay root-absolute
// (/404, /403, /login, the personal /settings tabs) while others are
// portal-relative and use <PortalNavigate> instead.
export function relativize(routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => {
    const next: RouteObject = { ...route }
    if (typeof route.path === 'string' && route.path.startsWith('/')) {
      if (process.env.NODE_ENV !== 'production' && route.path === '/') {
        throw new Error(
          'relativize: a nested route path of "/" is ambiguous with an index route',
        )
      }
      // Cast: RouteObject is a union whose index-route arm types `path` as
      // undefined, so TS won't narrow the assignment on the shared shape.
      ;(next as { path?: string }).path = route.path.slice(1)
    }
    if (route.children) {
      ;(next as { children?: RouteObject[] }).children = relativize(route.children)
    }
    return next
  })
}
